import { useLazyQuery } from '@apollo/client'
import type { ApolloError } from '@apollo/client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  CATALOG_PAGE_SIZE,
  GET_CATALOG_PAGE,
  SEARCH_PRODUCTS,
  hasMoreCatalogPages,
  type CatalogCategoryInput,
  type ICatalogPage,
  type ICatalogPageVariables,
  type ISearchProducts,
  type ISearchProductsVariables,
  type SearchFilterInput,
} from '@commerce/core'
import { useDebouncedValue } from './useDebouncedValue'

/** The chip that clears the category filter. Not a category — no query sees it. */
export const ALL = 'Todos'

/**
 * The chip that keeps only promoted products. Also not a category: it becomes
 * `filter.promotions` on the search field, which is why picking it switches the
 * page from browsing to searching without any text.
 */
export const PROMOTIONS = 'Promociones'

/** Long enough that a typed word is one request, short enough to feel typed. */
const SEARCH_DEBOUNCE_MS = 350

/**
 * Search results arrive flat, and `toCatalogItemInputs` reads category blocks.
 * The label never reaches the screen — the grid's own heading says "Resultados"
 * — but it cannot be empty, because a block with no category name is dropped by
 * `toCategoryFilters`.
 */
const SEARCH_BLOCK = 'Resultados'

interface LoadedPages {
  /** Which view these pages belong to; a change invalidates them. */
  view: string
  pages: CatalogCategoryInput[][]
  hasMore: boolean
}

export interface UseCatalogBrowseArgs {
  /**
   * Page one of the default view, as the boot query already fetched it. The
   * storefront boots on `GET_INITIAL_CATALOG_VALUES` (catalog, cart and session
   * in one round trip) and re-asking for those products through
   * `GET_CATALOG_PAGE` would be a second request for a page already painted.
   */
  bootBlocks: readonly CatalogCategoryInput[] | null | undefined
}

export interface UseCatalogBrowse {
  category: string
  setCategory: (category: string) => void
  /** The raw input value — the query only ever sees it debounced. */
  searchTerm: string
  setSearchTerm: (term: string) => void
  brands: readonly string[]
  setBrands: (brands: readonly string[]) => void
  clearFilters: () => void
  /** Whether anything is narrowing the grid: text, a brand, or the promo chip. */
  searching: boolean
  /** Every page of the current view, oldest first, ready for `toCatalogItemInputs`. */
  blocks: CatalogCategoryInput[]
  /** True while the *first* page of a newly chosen view is in flight. */
  loading: boolean
  /** True while a further page is in flight; the grid keeps what it has. */
  loadingMore: boolean
  hasMore: boolean
  loadMore: () => void
  error: ApolloError | undefined
}

/**
 * Browsing and searching the catalog on the server rather than in the browser.
 *
 * What it replaces: the grid used to hold the first page of the catalog and do
 * everything locally — `searchCatalog` over the loaded models, a category filter
 * built from the categories that page happened to include. Both lied by
 * omission on any storefront bigger than one page, and neither could reach a
 * product the boot query never returned.
 *
 * Two documents, because the field that can filter is not the field that browses:
 *
 * - `GET_CATALOG_PAGE` — the plain catalog, narrowed by `categoryName`. This is
 *   the browse path, and it is the one the storefront has always used.
 * - `SEARCH_PRODUCTS` — the paginated `search` field, which takes text *and*
 *   `SearchFilterInput` (categories, brands, promotions). Any narrowing beyond a
 *   single category goes through here.
 *
 * Pages accumulate in state rather than through Apollo's `fetchMore`, which
 * would need a `merge` field policy per root field and would then merge pages of
 * *different* views into one another — the cache keys `catalog(category:)` and
 * `search(searchTerm:)` by their arguments, and the shopper changing a chip is a
 * new list, not more of the old one. Keying the accumulator by view makes that
 * explicit, and a late answer to a view the shopper already left is dropped
 * instead of appended to the wrong list.
 */
export const useCatalogBrowse = ({ bootBlocks }: UseCatalogBrowseArgs): UseCatalogBrowse => {
  const { slug = '', sessionId = '' } = useParams()

  const [category, setCategory] = useState(ALL)
  const [searchTerm, setSearchTerm] = useState('')
  const [brands, setBrands] = useState<readonly string[]>([])

  const term = useDebouncedValue(searchTerm.trim(), SEARCH_DEBOUNCE_MS)

  const promotionsOnly = category === PROMOTIONS
  const categoryFilter = category === ALL || promotionsOnly ? undefined : category
  const searching = term !== '' || brands.length > 0 || promotionsOnly

  /**
   * The identity of what is on screen. Sorted brands so picking A then B is the
   * same view as picking B then A, which would otherwise refetch on reorder.
   */
  const view = useMemo(
    () =>
      JSON.stringify({
        category: categoryFilter ?? '',
        term,
        brands: [...brands].sort(),
        promotionsOnly,
      }),
    [categoryFilter, term, brands, promotionsOnly],
  )

  /** The default view is the one the boot query already paid for. */
  const isBootView = !searching && categoryFilter === undefined

  const [loaded, setLoaded] = useState<LoadedPages>({ view, pages: [], hasMore: false })
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<ApolloError | undefined>(undefined)

  const [runCatalogPage] = useLazyQuery<ICatalogPage, ICatalogPageVariables>(GET_CATALOG_PAGE)
  const [runSearch] = useLazyQuery<ISearchProducts, ISearchProductsVariables>(SEARCH_PRODUCTS)

  /** The view a request was issued for, so a stale answer can be recognised. */
  const inFlightFor = useRef<string | null>(null)

  /**
   * The same "a request is out" fact as `pending`, in a ref because `loadMore`
   * has to read it *synchronously*. React batches state, so two calls in one
   * tick — the sentinel firing twice as the page settles, or a double tap on
   * the button — both see `pending: false` and both ask for the same page.
   */
  const busy = useRef(false)

  const pagination = useCallback(
    (pageNumber: number) => ({ pageNumber, pageSize: CATALOG_PAGE_SIZE }),
    [],
  )

  /**
   * Appends a page — but only to the view it was requested for. Without the
   * guard, a slow answer for "Bebidas" lands in the grid after the shopper has
   * already moved to "Abarrotes", and the count says 40 products of which half
   * are the wrong ones.
   */
  const appendPage = useCallback(
    (forView: string, blocks: CatalogCategoryInput[], more: boolean) => {
      setLoaded((current) => {
        if (current.view !== forView) return current
        return { view: forView, pages: [...current.pages, blocks], hasMore: more }
      })
    },
    [],
  )

  const fetchPage = useCallback(
    async (pageNumber: number, forView: string) => {
      inFlightFor.current = forView
      busy.current = true
      setPending(true)
      setError(undefined)

      try {
        if (searching) {
          const filter: SearchFilterInput = {
            ...(categoryFilter ? { categories: [categoryFilter] } : {}),
            ...(brands.length > 0 ? { brands: [...brands] } : {}),
            ...(promotionsOnly ? { promotions: true } : {}),
          }

          const { data, error: failure } = await runSearch({
            variables: {
              storefrontName: slug,
              sessionUid: sessionId,
              ...(term === '' ? {} : { searchTerm: term }),
              ...(Object.keys(filter).length > 0 ? { filter } : {}),
              pagination: pagination(pageNumber),
            },
          })

          if (failure) throw failure

          const products = data?.search?.products ?? []
          const block: CatalogCategoryInput = { category: SEARCH_BLOCK, productsPrices: products }
          // The server reports `hasNextPage`; the length check is the fallback
          // for a storefront whose `search` answers without a pagination block.
          const more =
            data?.search?.pagination?.hasNextPage ?? products.length === CATALOG_PAGE_SIZE

          appendPage(forView, [block], more)
        } else {
          const { data, error: failure } = await runCatalogPage({
            variables: {
              storefrontName: slug,
              sessionUid: sessionId,
              ...(categoryFilter ? { categoryName: categoryFilter } : {}),
              pagination: pagination(pageNumber),
            },
          })

          if (failure) throw failure

          const blocks = data?.catalog ?? []
          appendPage(forView, blocks, hasMoreCatalogPages(blocks, CATALOG_PAGE_SIZE))
        }
      } catch (failure) {
        // A failed page keeps the pages already on screen: the shopper can
        // still buy what they can see, and the alert says what went wrong.
        if (inFlightFor.current === forView) setError(failure as ApolloError)
      } finally {
        if (inFlightFor.current === forView) {
          busy.current = false
          setPending(false)
        }
      }
    },
    [
      appendPage,
      brands,
      categoryFilter,
      pagination,
      promotionsOnly,
      runCatalogPage,
      runSearch,
      searching,
      sessionId,
      slug,
      term,
    ],
  )

  // A new view resets the accumulator, then loads its first page — except the
  // boot view, whose first page is `bootBlocks`.
  useEffect(() => {
    setLoaded({ view, pages: [], hasMore: false })
    setError(undefined)
    if (!isBootView) void fetchPage(1, view)
    // Deliberately not depending on `fetchPage`: it is rebuilt from the very
    // inputs `view` is derived from, so listing it would run this effect twice
    // for every change — two requests for one chip.
  }, [view, isBootView])

  const bootPage = useMemo<CatalogCategoryInput[]>(() => [...(bootBlocks ?? [])], [bootBlocks])

  const blocks = useMemo(
    () => (isBootView ? [...bootPage, ...loaded.pages.flat()] : loaded.pages.flat()),
    [isBootView, bootPage, loaded.pages],
  )

  const pagesLoaded = (isBootView ? 1 : 0) + loaded.pages.length

  const hasMore = useMemo(() => {
    if (loaded.pages.length > 0) return loaded.hasMore
    if (isBootView) return hasMoreCatalogPages(bootPage, CATALOG_PAGE_SIZE)
    return false
  }, [loaded, isBootView, bootPage])

  const loadMore = useCallback(() => {
    if (busy.current || !hasMore) return
    void fetchPage(pagesLoaded + 1, view)
  }, [hasMore, fetchPage, pagesLoaded, view])

  const clearFilters = useCallback(() => {
    setBrands([])
    setSearchTerm('')
    setCategory(ALL)
  }, [])

  return {
    category,
    setCategory,
    searchTerm,
    setSearchTerm,
    brands,
    setBrands,
    clearFilters,
    searching,
    blocks,
    // The first page of a chosen view: nothing is on screen yet, so this is the
    // one case that earns a spinner in place of the grid.
    loading: pending && pagesLoaded === 0,
    loadingMore: pending && pagesLoaded > 0,
    hasMore,
    loadMore,
    error,
  }
}
